import { DateTime } from 'luxon';
import { MockedRequest, rest } from 'msw';
import { getEnv } from 'util/getEnv';
import { imageUploads, uploads } from './fixtures/image-uploads.fixture';
import { CreateImageUploadRequest } from './menu-service.types';
import { menuImports } from './fixtures/menu-imports.fixtures';
import { makeCursorResults } from 'test-util/api/response';
import { targetingItems } from './fixtures/targeting-items.fixture';
import { targetingGroups } from './fixtures/targeting-groups.fixture';
import { targetingMenus } from './fixtures/targeting-menus.fixture';

export const listImageUploadsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/bulkimageuploads`,
    fn,
  );

export const getImageUploadMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/bulkimageuploads/:id`,
    fn,
  );

export const createImageUploadMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/bulkimageuploads`,
    fn,
  );

export const updateImageUploadMock = fn =>
  rest.put(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/bulkimageuploads/:id`,
    fn,
  );

export const processImageUploadMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/bulkimageuploads/:id/process`,
    fn,
  );
export const getPastImportsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/locations/:locationId/pastimports`,
    fn,
  );

export const listMenuImportsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/merchants/:merchantId/mostrecentimports`,
    fn,
  );

export const runFullImportMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/locations/:locationId/runfullimport`,
    fn,
  );

export const runAllImportMock = fn =>
  rest.post(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/runallimports`,
    fn,
  );

export const searchProductsMock = fn =>
  rest.post(
    `${getEnv('REACT_APP_ADMIN_API_URL')}/incentivio-menu-service/search`,
    fn,
  );

export const searchProductLocationsMock = fn =>
  rest.get(
    `${getEnv(
      'REACT_APP_ADMIN_API_URL',
    )}/incentivio-menu-service/search/locations`,
    fn,
  );

export const handlers = () => [
  listImageUploadsMock((_req, res, ctx) => {
    return res(ctx.json(imageUploads));
  }),

  getImageUploadMock((req, res, ctx) => {
    return res(
      ctx.json({
        bulkImageUpload: imageUploads.uploads.find(
          upload => upload.id === req.params.id,
        ),
      }),
    );
  }),

  createImageUploadMock(
    (req: MockedRequest<CreateImageUploadRequest>, res, ctx) => {
      const id = (uploads.length + 1).toString();
      uploads.push({
        ...req.body,
        id,
        clientId: req.headers.get('Inc-Client-Id'),
        dateCreated: DateTime.utc().toISO(),
        dateUpdated: DateTime.utc().toISO(),
        status: 'UPLOADING' as const,
      });
      return res(ctx.json({ id }));
    },
  ),

  updateImageUploadMock((req, res, ctx) => {
    const imageUpload = uploads.find(upload => upload.id === req.params.id);
    imageUpload.status = req.body.status;
    imageUpload.statusMessage = req.body.statusMessage;
    imageUpload.processedFiles = req.body.processedFiles;

    return res(ctx.status(204));
  }),

  processImageUploadMock((req, res, ctx) => {
    const imageUpload = uploads.find(upload => upload.id === req.params.id);
    imageUpload.status = 'PROCESSING';
    return res(ctx.status(204));
  }),

  getPastImportsMock((_req, res, ctx) => {
    return res(ctx.json(menuImports));
  }),

  listMenuImportsMock((_req, res, ctx) => {
    return res(ctx.json(menuImports));
  }),

  runFullImportMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  runAllImportMock((_req, res, ctx) => {
    return res(ctx.status(200));
  }),

  searchProductsMock((req, res, ctx) => {
    const fixtureMap = {
      ITEM: targetingItems.results,
      GROUP: targetingGroups.results,
      MENU: targetingMenus.results,
    };
    const all = fixtureMap[req.body.productType] ?? [];
    const results = all.map(({ locations, ...rest }) => rest);
    return res(ctx.json(makeCursorResults(results, req.body)));
  }),

  searchProductLocationsMock((req, res, ctx) => {
    const productType = req.url.searchParams.get('productType');
    const extTargetingId = req.url.searchParams.get('extTargetingId');
    const fixtureMap = {
      ITEM: targetingItems.results,
      GROUP: targetingGroups.results,
      MENU: targetingMenus.results,
    };
    const all = fixtureMap[productType] ?? [];
    const product = all.find(r => r.extTargetingId === extTargetingId);
    return res(
      ctx.delay(800),
      ctx.json({
        extTargetingId,
        productType,
        locations: product?.locations ?? [],
      }),
    );
  }),
];
